import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { NavLink } from 'react-router-dom'
import ActionBar from '../../components/ActionBar'
import Search from '../../components/Search'
import Button from '../../components/shared/Button'
import { AddIcon } from '../../components/shared/Icons'
import Loader from '../../components/shared/Loader'
import Pagination from '../../components/shared/Pagination'
import Table from '../../components/Table'
import useFetch from '../../hooks/useFetch'
import CaptionLayout from '../../layouts/CaptionLayout'
import Section from '../../layouts/Section'
import SectionBody from '../../layouts/SectionBody'
import { getAllProducts } from '../../utils/Apis/products'
import { cn } from '../../utils/helperFunctions'
import { withErrorHandling } from '../../utils/withErrorHandling'
import { columns } from './COLUMNS'

const Products = ({ className, ...props }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [sortProduct, setSortProduct] = useState('old') // Default to 'old' to 'new'
  const [productSource, setProductSource] = useState('')

  const fetchFn = useMemo(() => (search, currentPage, sortProduct, source) =>
    getAllProducts(search, currentPage, sortProduct, source), [])

  const { data, error, loading, refetch } = useFetch({
    fn: fetchFn,
  })

  const handleSearch = withErrorHandling(async (search) => {
    refetch(search, currentPage, sortProduct, productSource)
  })

  const fetchProducts = useCallback(() => {
    refetch(search, currentPage, sortProduct, productSource)
  }, [search, currentPage, sortProduct, productSource, refetch])

  useEffect(() => {
    document.title = 'Products - Admin'
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  return (
    <div className={cn('', className)} {...props}>
      <Section title={'Products'} className={'lg:pb-2'}>
        <Search
          handleSearch={handleSearch}
          search={search}
          setSearch={setSearch}
          className={'top-0'}
        />
        <ActionBar className={'top-0'}>
          <div className="flex justify-start w-full">
              <select
                aria-label="Product source"
                className="mr-2 h-9 rounded-md border bg-background px-2"
                value={productSource}
                onChange={(event) => {
                  setCurrentPage(1)
                  setProductSource(event.target.value)
                }}
              >
                <option value="">All Sources</option>
                <option value="manual">Manual</option>
                <option value="spider-products">Spider Products</option>
                <option value="digikey-spider-products">DigiKey Spider Products</option>
              </select>
              <Button className={'mr-2'} onClick={() => setSortProduct('new')}>Sort by New</Button>
              <Button onClick={() => setSortProduct('old')}>Sort by Old</Button>
          </div>
          <div className="flex flex-col gap-2 md:flex-row">
            <NavLink to={'/spider-products'}>
              <Button className={'bg-green-600 hover:bg-green-700'}>
                Spider Preview
              </Button>
            </NavLink>
            <NavLink to={'/products/new'}>
              <Button>
                <AddIcon className={'h-6 w-6'} />
                Add Product
              </Button>
            </NavLink>
          </div>
        </ActionBar>
      </Section>

      {loading ? (
        <Loader />
      ) : error ? (
        <p>Error</p>
      ) : (
        <SectionBody>
          <CaptionLayout
            results={data?.totalCount}
            caption={'Products present'}
          >
            <Pagination
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pages={data?.paginationResult?.numberOfPages}
              showGoToPage={true}
            />
          </CaptionLayout>
          <div
            className={cn(
              'custom-scroll relative max-h-[45vh] w-full overflow-auto md:max-h-[60vh]',
              className,
            )}
            {...props}
          >
            <Table data={data?.data} columns={columns} />
            {/* Fade */}
            <div className="pointer-events-none sticky bottom-0 left-0 h-16 w-full bg-gradient-to-t from-background to-transparent" />
          </div>
        </SectionBody>
      )}
    </div>
  )
}

export default Products
